import {PatientsListPageMeta} from "./patients-list-page.interface";
import {PatientsSearchQuery, toQueryString} from "../../models/patients-search-query";
import {GetPatientsListPropertyOrderBy, PatientFilterType, PatientPriorityStatus} from "doctors-dashboard/http-clients/index";
import {ChangedSearchOption, ChangedSearchOptions, ControlValidator, FormControlValidators, PagingQuery} from "orpyx-web-common";

const PatientsListTermValidators = new ControlValidator([
    FormControlValidators.minLength(2),
    FormControlValidators.maxLength(32),
]);

/// <summary>
/// <para>Remembers the last sort a user picked on each tab for this session, so switching tabs and
/// back restores it instead of resetting to that tab's default (see
/// PatientsSearchQuery.DEFAULT_ORDER_BY_PATIENT_TYPE). Deliberately not persisted beyond the session -
/// a full reload is treated as a fresh visit.</para>
/// </summary>
const rememberedOrderByPerPatientType = new Map<PatientFilterType, {orderBy: GetPatientsListPropertyOrderBy, orderByAsc: boolean}>();

const rememberOrderBy = (patientsType: PatientFilterType | undefined, orderBy: GetPatientsListPropertyOrderBy | undefined, orderByAsc: boolean | undefined) => {
    if (patientsType && orderBy !== undefined && orderByAsc !== undefined) {
        rememberedOrderByPerPatientType.set(patientsType, {orderBy, orderByAsc});
    }
};

export const onPatientsListPageLoaded = (props: PatientsListPageMeta) => () => {
    const params = new URLSearchParams(props.location.search);
    const query = PatientsSearchQuery.parse(
        params.has('term') ? params.get('term')! : undefined,
        params.has(PatientsSearchQuery.PARAM_FILTER_BY) ? params.get(PatientsSearchQuery.PARAM_FILTER_BY)! : undefined,

        props.patientType,

        params.has(PatientsSearchQuery.PARAM_PRIORITY_STATUS) ? params.get(PatientsSearchQuery.PARAM_PRIORITY_STATUS)! : undefined,

        // Falls through to PatientsSearchQuery's per-tab default when absent from the URL.
        params.has(PatientsSearchQuery.PARAM_ORDER_BY) ? params.get(PatientsSearchQuery.PARAM_ORDER_BY)! : undefined,
        params.has(PatientsSearchQuery.PARAM_ORDER_BY_ASC) ? params.get(PatientsSearchQuery.PARAM_ORDER_BY_ASC)! : undefined,

        params.has(PatientsSearchQuery.PARAM_OFFSET) ? params.get(PatientsSearchQuery.PARAM_OFFSET)! : undefined,
        params.has(PatientsSearchQuery.PARAM_COUNT) ? params.get(PatientsSearchQuery.PARAM_COUNT)! : undefined,
    );

    props.refreshPatientsList(query);
};


export const onPatientsListTermValidation = (term: string | undefined) => {
    return PatientsListTermValidators.validate(term);
};

const generatePatientsSearchQuery = (props: PatientsListPageMeta, options: ChangedSearchOptions): PatientsSearchQuery => {
    if (options.changed === ChangedSearchOption.Term) {
       return  {
            ...props.search,
            term: options.term,
            offset: PagingQuery.DEFAULT_PAGE
        };
    } else if (options.changed === ChangedSearchOption.OrderBy) {
        return  {
            ...props.search,
            term: options.term,
            orderBy: options.orderBy!.value,
            orderByAsc: options.orderBy!.orderByAsc ?? PatientsSearchQuery.DEFAULT_ORDER_BY_METHOD
        };
    } else if (options.changed === ChangedSearchOption.FilterBy) {
        return  {
            ...props.search,
            term: options.term,
            filterBy: options.filterBy!.key,
            offset: PagingQuery.DEFAULT_PAGE
        };
    }
    throw new Error(`Invalid ChangedSearchOption ${options.changed}`)
};

export const onPatientsListSearchOptionsChanged = (props: PatientsListPageMeta) => (options: ChangedSearchOptions): void => {
    const searchQuery = generatePatientsSearchQuery(props, options);
    if (options.changed === ChangedSearchOption.OrderBy) {
        rememberOrderBy(props.search.patientsType, searchQuery.orderBy, searchQuery.orderByAsc);
    }
    onSearchQueryChanged(props, searchQuery);
};

export const onPatientsListPatientTypeChanged = (props: PatientsListPageMeta) => (patientFilterType: PatientFilterType) => {
    // Each tab has its own default sort (see PatientsSearchQuery.DEFAULT_ORDER_BY_PATIENT_TYPE), and
    // the previous tab's selection may not even be a valid option on the new tab (e.g. LastReviewDate
    // only exists on Active). Restore whatever sort the user last picked on the target tab, if any;
    // otherwise leave orderBy/orderByAsc out of the URL so PatientsSearchQuery.parse falls through to
    // that tab's default rather than inheriting the tab being left.
    const remembered = rememberedOrderByPerPatientType.get(patientFilterType);
    const searchQuery: PatientsSearchQuery = {
        ...props.search,
        patientsType: patientFilterType,
        orderBy: remembered?.orderBy,
        orderByAsc: remembered?.orderByAsc,
        offset: PagingQuery.DEFAULT_PAGE
    };
    onSearchQueryChanged(props, searchQuery);
};

export const onPatientsListPriorityStatusChanged = (props: PatientsListPageMeta) => (priorityStatus: PatientPriorityStatus) => {
    const searchQuery: PatientsSearchQuery = {...props.search, priorityStatus: priorityStatus, offset: PagingQuery.DEFAULT_PAGE};
    onSearchQueryChanged(props, searchQuery);
};

export const onPatientsListPageChanged = (props: PatientsListPageMeta) => (page: number) => {
    const searchQuery: PatientsSearchQuery = {...props.search, offset: page};
    onSearchQueryChanged(props, searchQuery);
};

export const onPatientsListCountChanged = (props: PatientsListPageMeta) => (count: number) => {
    const searchQuery: PatientsSearchQuery = {...props.search, count: count, offset: PagingQuery.DEFAULT_PAGE};
    onSearchQueryChanged(props, searchQuery);
};

export const onSearchQueryChanged = (props: PatientsListPageMeta, searchQuery: PatientsSearchQuery) => {
    const urlQuery: string = toQueryString(searchQuery);
    props.history!.push(urlQuery);
};